import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const MemberEditModal = ({ isOpen, onClose, onSave, member }) => {
  const isCreateMode = !member;
  const [formData, setFormData] = useState({
    name: member?.name || '',
    phone: member?.phone || '',
    skillLevel: member?.skillLevel || '',
    membershipType: member?.membershipType || '',
    membershipExpiry: member?.membershipExpiry || '',
    notes: member?.notes || ''
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    if (member) {
      setFormData({
        name: member?.name || '',
        phone: member?.phone || '',
        skillLevel: member?.skillLevel || '',
        membershipType: member?.membershipType || '',
        membershipExpiry: member?.membershipExpiry || '',
        notes: member?.notes || ''
      });
    } else {
      setFormData({
        name: '',
        phone: '',
        skillLevel: '',
        membershipType: 'regular',
        membershipExpiry: '',
        notes: ''
      });
    }

    setErrors({});
  }, [isOpen, member]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const membershipTypeOptions = [
    { value: 'regular', label: '일반 회원' },
    { value: 'trial', label: '체험 회원' }
  ];

  const skillLevelOptions = [
    { value: '1.0', label: '1.0 - 입문' },
    { value: '1.5', label: '1.5 - 초급' },
    { value: '2.0', label: '2.0 - 초급+' },
    { value: '2.5', label: '2.5 - 초중급' },
    { value: '3.0', label: '3.0 - 중급' },
    { value: '3.5', label: '3.5 - 중급+' },
    { value: '4.0', label: '4.0 - 고급' },
    { value: '4.5', label: '4.5 - 고급+' },
    { value: '5.0', label: '5.0 - 전문가' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handlePhoneChange = (e) => {
    const value = e?.target?.value?.replace(/[\D]/g, '') || '';
    const digits = value.slice(0, 11);
    let formatted = digits;
    if (digits.length <= 3) {
      formatted = digits;
    } else if (digits.length <= 7) {
      formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
    } else {
      formatted = `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
    }
    handleChange('phone', formatted);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = '이름을 입력해주세요';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = '전화번호를 입력해주세요';
    } else if (!/^01[0-9]-?[0-9]{3,4}-?[0-9]{4}$/?.test(formData?.phone?.replace(/\s/g, ''))) {
      newErrors.phone = '올바른 전화번호 형식이 아닙니다';
    }

    if (!formData?.skillLevel) {
      newErrors.skillLevel = '실력 레벨을 선택해주세요';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      await onSave({ ...(member || {}), ...formData });
      onClose();
    } catch (error) {
      setErrors({ submit: '저장 중 오류가 발생했습니다' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e?.target === e?.currentTarget && !isLoading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-300 flex items-start justify-center pt-[8vh] px-4 overflow-y-auto"
      onClick={handleBackdropClick}
    >
      <div className="fixed inset-0 bg-background z-250" aria-hidden="true" />
      <div className="relative z-300 w-full max-w-2xl bg-card rounded-xl shadow-2xl border border-border my-8">
        <div className="p-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-2xl font-heading font-semibold text-foreground mb-2">
                {isCreateMode ? '회원 추가' : '회원 정보 수정'}
              </h2>
              <p className="text-sm caption text-muted-foreground">
                {isCreateMode ? '새 회원을 추가합니다' : '회원의 정보를 수정합니다'}
              </p>
            </div>
            <button
              onClick={onClose}
              disabled={isLoading}
              className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-athletic active-press disabled:opacity-50"
              aria-label="닫기"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="이름"
                type="text"
                placeholder="홍길동"
                value={formData?.name}
                onChange={(e) => handleChange('name', e?.target?.value)}
                error={errors?.name}
                required
                disabled={isLoading}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="전화번호"
                type="tel"
                placeholder="010-1234-5678"
                value={formData?.phone}
                onChange={handlePhoneChange}
                error={errors?.phone}
                required
                disabled={isLoading}
              />
            </div>

            <Select
              label="실력 레벨"
              options={skillLevelOptions}
              value={formData?.skillLevel}
              onChange={(value) => handleChange('skillLevel', value)}
              error={errors?.skillLevel}
              required
              disabled={isLoading}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="회원권 유형"
                options={membershipTypeOptions}
                value={formData?.membershipType}
                onChange={(value) => handleChange('membershipType', value)}
                disabled={isLoading}
              />

              {formData?.membershipType === 'regular' && (
                <Input
                  label="회원권 만료일"
                  type="date"
                  value={formData?.membershipExpiry}
                  onChange={(e) => handleChange('membershipExpiry', e?.target?.value)}
                  disabled={isLoading}
                />
              )}
            </div>

            <Input
              label="메모"
              type="text"
              placeholder="추가 정보나 메모"
              value={formData?.notes}
              onChange={(e) => handleChange('notes', e?.target?.value)}
              disabled={isLoading}
            />

            {errors?.submit && (
              <div className="bg-error/10 border border-error/20 rounded-md p-4 flex items-start gap-3">
                <Icon name="AlertCircle" size={20} color="var(--color-error)" />
                <p className="text-sm text-error">{errors?.submit}</p>
              </div>
            )}

            <div className="flex gap-3 pt-4 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isLoading}
                fullWidth
              >
                취소
              </Button>
              <Button
                type="submit"
                variant="default"
                loading={isLoading}
                iconName="Save"
                iconPosition="right"
                fullWidth
              >
                {isCreateMode ? '추가' : '저장'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MemberEditModal;